// Imports for external libraries go here.
import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';

// separated by a blank line from external imports.
// The closer the import is to the file, the lower it should be in this list.
import { StyledNavigationContainer, StyledNavigationPlaceholder } from './NavigationContainer.styles';
import { NavigationContainerProps } from './NavigationContainer.types';
import { NavigationContext } from './NavigationContextProvider';
import { LanguageSelectorContext } from '../../molecules/Navigation/LanguageSelector/LanguageSelectorContextProvider';
import { MyTripsModal } from '../../molecules/Navigation/MyTrips';
import { NavigationWrapper, NavigationUtilityLinkWrapper, UtilityNavigationType } from '../../molecules/Navigation';
import { hotelLogoQuery, propertyBarAuthQuery, propertyBarQuery } from './queries';
import { CACHE_MARRIOTT_URL, HOTEL_LOGO_ALT_TEXT } from '../../utils/constants';
import HWSSecondaryNavigationMobile from '../../molecules/SecondaryNavigation/HWSPrimaryDetachedNavigation/HWSSecondaryNavigationMobile/HWSSecondaryNavigationMobile';
import { useSecondaryNavigationStore } from '../../store';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { useClientEnvVarsStore, useScrollStore, useStickyState, useUserDetailsStore } from '@marriott/mi-store-utils';
import { HeaderLogo, AEMModel, canUseDOM, Image } from '@marriott/mi-ui-library';
import { useSession } from '@marriott/mi-headless-utils';
import { addSubDirectoryPrefix, getSubDirectoryLocale } from '../../utils';

export const NavigationContainer: FC<NavigationContainerProps> = props => {
  const [isWhiteHeader, setIsWhiteHeader] = useState<boolean>(false);
  const [isMegaMenuActive, setIsMegaMenuActive] = useState<boolean>(false);
  const { model, cqPath, isAuthorMode } = props;
  const styleclass = model?.styleclass;
  const { logoUrl, logoImageAltText, logoType } = model?.resourceProperties ?? {};
  const primaryNavigationProps: AEMModel = model?.cqItems ? model.cqItems['primaryNavigation'] : {};
  const hasNavPrimaryPresent =
    !!primaryNavigationProps &&
    ((Array.isArray(primaryNavigationProps[':itemsOrder']) && primaryNavigationProps[':itemsOrder'].length > 0) ||
      Object.keys((primaryNavigationProps[':items'] as Record<string, unknown>) || {}).length > 0);
  const isTransparentHeader = model?.resourceProperties?.transparency === 'true';
  const utilityNavigation = (model?.cqItems ? model.cqItems['utilityNavigation'] : {}) as UtilityNavigationType;
  const setRegisteredComponent = useScrollStore(state => state.setRegisteredComponent);
  const navComponentOrder = useScrollStore(state => state.navComponentOrder);
  const [isSearchPresent, setIsSearchPresent] = useState(false);
  const { offset, isSticky } = useStickyState(model?.componentId);
  const { isSignedInUser } = useUserDetailsStore(state => ({
    isSignedInUser: state.isSignedInUser,
  }));
  const { envVarsObject } = useClientEnvVarsStore();
  const { datalayer, session } = useSession();
  const locale = envVarsObject?.['ACCEPT_LANGUAGE'];
  const isBrandedVariation = model?.resourceProperties?.branded === 'true';
  const isSimplifiedNavigation = !isBrandedVariation && !hasNavPrimaryPresent;
  const { query } = useRouter();
  // IMPORTANT: access via bracket notation due to index signature typing
  const qpRaw = query['propertyCode'] as string | string[] | undefined;
  const qpPropertyCode = (Array.isArray(qpRaw) ? qpRaw[0] : qpRaw || '').toUpperCase();
  const qpPropertyId = query['propertyId'] as string | undefined;
  const [headerHeight, setHeaderHeight] = useState(0);
  const componentId = model?.componentId;
  const [fallbackSticky, setFallbackSticky] = useState(false);
  const registeredComponents = useScrollStore(state => state.registeredComponents);
  const isRegisteredInStore = useScrollStore(state =>
    componentId ? !!state.registeredComponents[componentId] : false
  );
  const [isSignInDrawerOpen, setIsSignInDrawerOpen] = useState<boolean>(false);
  const [isMainMenuOpen, setIsMainMenuOpen] = useState<boolean>(false);
  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState<boolean>(false);
  const [isMyTripsModalOpen, setIsMyTripsModalOpen] = useState<boolean>(false);
  const [isAccountLoggedInPanelEnabled, setIsAccountLoggedInPanelEnabled] = useState<boolean>(false);
  const hasOpenOverlay =
    isMegaMenuActive || isSignInDrawerOpen || isMainMenuOpen || isLanguageSelectorOpen || isMyTripsModalOpen;
  const scrollPosition = useScrollStore(state => state.scrollPosition);
  const componentOrderIndex = componentId ? navComponentOrder.findIndex(id => id === componentId) : -1;
  const isGeneralMessagePresent =
    canUseDOM && !!document.querySelector('[data-component-name="o-common-static-generalmessage"]');
  const hasGeneralMessageDownOnlyAbove =
    componentOrderIndex > 0 &&
    navComponentOrder.slice(0, componentOrderIndex).some(id => {
      const component = registeredComponents[id];
      return !!component && component.isStickyOnScrollDown && !component.isStickyOnScrollUp;
    });
  const stickyTop = isGeneralMessagePresent && hasGeneralMessageDownOnlyAbove ? 0 : offset;
  const shouldUseStoreSticky = !!componentId && navComponentOrder.includes(componentId) && isRegisteredInStore;
  const sticky = shouldUseStoreSticky ? isSticky : fallbackSticky;
  // find the property id from the global store with fallback to datalayer window object
  const propertyId =
    (canUseDOM &&
      ((qpPropertyCode ||
        qpPropertyId ||
        session?.data?.AriesSearch?.searchCriteria?.propertyId ||
        datalayer?.['prop_marsha_code'] ||
        datalayer?.['mrshaCode']) as string | undefined)) ||
    'NYCES';

  const variables: { propertyId: string } = { propertyId: propertyId as string };

  // fetch hotel logo
  const { data } = useQuery(hotelLogoQuery, {
    context: {
      headers: {},
    },

    skip: !isBrandedVariation || !propertyId,
    variables,
    onError: error => console.error('Error fetching brand logo', error),
  });
  // fetch data hotel data for either signed in or signed out users.
  const { data: hotelData } = useQuery(isSignedInUser ? propertyBarAuthQuery : propertyBarQuery, {
    fetchPolicy: isSignedInUser ? 'no-cache' : 'cache-first',
    context: {
      headers: {
        'accept-language': locale,
      },
    },
    skip: !isBrandedVariation || !propertyId,
    variables,
    onError: error => console.error('Error fetching property data', error),
  });

  const hotelLogoUrl = data?.property?.media?.hotelLogo?.edges?.[0]?.node?.imageSrc ?? '';

  const navRef = useRef(null);
  useEffect(() => {
    if (!canUseDOM) return;

    const checkSearch = () => {
      const searchEl = document.querySelector(
        '[data-component-name="o-shop-searchform"], [data-component-name="ng-shop-searchform"]'
      );
      const found = !!searchEl;
      setIsSearchPresent(prev => (prev !== found ? found : prev));
    };

    checkSearch();
    const observer = new MutationObserver(checkSearch);
    observer.observe(document.body, { childList: true, subtree: false });
    return () => observer.disconnect();
  }, []);
  const headerContainerRef = useCallback((ref: HTMLDivElement) => {
    if (!ref) {
      return;
    }
    setHeaderHeight(ref.clientHeight);
  }, []);

  useEffect(() => {
    if (!model?.componentId || !headerHeight || !navComponentOrder.includes(model.componentId)) {
      return;
    }
    setRegisteredComponent({
      componentId: model.componentId,
      isStickyOnScrollUp: !!model?.scrollSetting?.isStickyOnScrollUp,
      isStickyOnScrollDown: !!model?.scrollSetting?.isStickyOnScrollDown,
      height: headerHeight,
    });
  }, [
    headerHeight,
    model?.componentId,
    model?.scrollSetting?.isStickyOnScrollDown,
    model?.scrollSetting?.isStickyOnScrollUp,
    navComponentOrder,
    setRegisteredComponent,
  ]);

  useEffect(() => {
    if (!canUseDOM || typeof window === 'undefined') return;
    if (!shouldUseStoreSticky) return; // only register store handler when store is active
    const handleScroll = useScrollStore.getState().handleScroll;
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [shouldUseStoreSticky]);

  useEffect(() => {
    if (shouldUseStoreSticky || !canUseDOM || typeof window === 'undefined') return;
    const onScroll = () => {
      /* ... existing fallback logic ... */
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [model?.scrollSetting?.isStickyOnScrollDown, model?.scrollSetting?.isStickyOnScrollUp, shouldUseStoreSticky]);

  useEffect(() => {
    if (shouldUseStoreSticky || !canUseDOM || typeof window === 'undefined') {
      return;
    }

    let previousY = window.scrollY || 0;
    const EDGE_TOLERANCE_PX = 2;
    const onScroll = () => {
      const currentY = window.scrollY || 0;
      const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const isAtBottom = currentY >= maxScrollY - EDGE_TOLERANCE_PX;
      if (isAtBottom && currentY >= previousY) {
        previousY = currentY;
        return;
      }
      if (currentY <= 0) {
        setFallbackSticky(false);
      } else if (currentY > previousY) {
        setFallbackSticky(!!model?.scrollSetting?.isStickyOnScrollDown);
      } else if (currentY < previousY) {
        setFallbackSticky(!!model?.scrollSetting?.isStickyOnScrollUp);
      }
      previousY = currentY;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [model?.scrollSetting?.isStickyOnScrollDown, model?.scrollSetting?.isStickyOnScrollUp, shouldUseStoreSticky]);

  useEffect(() => {
    setIsWhiteHeader(hasOpenOverlay || scrollPosition > 0);
  }, [hasOpenOverlay, scrollPosition]);

  const isInverse = (!sticky && !isWhiteHeader && !hasOpenOverlay && isTransparentHeader) || isBrandedVariation;
  const secondaryNavigationData = useSecondaryNavigationStore(state => state.secondaryNavigationData);
  // ensure the Branch banner z-index is lowered when the main menu or sign in drawer is open
  useEffect(() => {
    const branchBanner = document.getElementById('branch-banner-iframe');
    const reset = () => {
      branchBanner?.style.removeProperty('z-index');
    };

    if (isMainMenuOpen || isSignInDrawerOpen) {
      branchBanner?.style.setProperty('z-index', '50');
    } else {
      reset();
    }

    return () => {
      reset();
    };
  }, [isMainMenuOpen, isSignInDrawerOpen]);

  return (
    <NavigationContext.Provider
      value={{
        offset,
        navRef,
        isInverse,
        isSignInDrawerOpen,
        setIsSignInDrawerOpen,
        isMainMenuOpen,
        setIsMainMenuOpen,
        isMyTripsModalOpen,
        setIsMyTripsModalOpen,
        isAccountLoggedInPanelEnabled,
        setIsAccountLoggedInPanelEnabled,
      }}
    >
      <LanguageSelectorContext.Provider value={{ isLanguageSelectorOpen, setIsLanguageSelectorOpen }}>
        <StyledNavigationContainer
          id="navcontainer"
          data-component-name="o-common-static-navigationcontainer"
          ref={headerContainerRef}
          isBrandedVariation={isBrandedVariation}
          onMouseOver={() => setIsWhiteHeader(true)}
          offset={offset}
          stickyTop={stickyTop}
          onFocus={() => {
            setIsWhiteHeader(true);
          }}
          onMouseOut={() => {
            if (!hasOpenOverlay) {
              setIsWhiteHeader(false);
            }
          }}
          className={clsx(styleclass, {
            'default-background inverse': isInverse,
            'header-sticky t-background-color': sticky,
            'header-transparent': isTransparentHeader,
            'header-main-menu-open': isMainMenuOpen,
            'standard t-background-color': (isWhiteHeader && !sticky) || !isTransparentHeader || isMainMenuOpen,
            'is-overlay': hasOpenOverlay,
            'brand-css': isBrandedVariation,
          })}
        >
          <header role="banner" ref={navRef}>
            <div
              className={clsx('banner-row row align-items-center mx-0', { 'color-scheme7 h-100': isBrandedVariation })}
            >
              <div
                className={clsx('navigation-container', {
                  'container-lg container-xl p-0': !styleclass?.includes('standard'),
                })}
              >
                <div
                  className={clsx('navigation-logo-container', {
                    'branded-logo-container': isBrandedVariation,
                    'branded-logo-container--hws-detached':
                      isBrandedVariation && secondaryNavigationData?.model?.variation === 'hws primary detached',
                  })}
                >
                  {secondaryNavigationData?.model &&
                    secondaryNavigationData?.model?.variation === 'hws primary detached' && (
                      <div className="d-lg-none" data-component-name="o-secondary-navigation-mobile">
                        <HWSSecondaryNavigationMobile
                          {...secondaryNavigationData.model}
                          activeMenuItem={secondaryNavigationData.activeMenuItem}
                        />
                      </div>
                    )}
                  {!isBrandedVariation ? (
                    <HeaderLogo
                      logoUrl={logoUrl}
                      logoImageAltText={logoImageAltText}
                      logoType={logoType}
                      customClassName={isSimplifiedNavigation ? 'simplified-mobile-logo' : 'ml-3 ml-lg-0'}
                    />
                  ) : (
                    <a
                      href={addSubDirectoryPrefix(
                        `${getSubDirectoryLocale(locale)}/hotels/${hotelData?.property?.seoNickname}/overview`
                      )}
                    >
                      <Image
                        defaultImageURL={`${CACHE_MARRIOTT_URL}${hotelLogoUrl}`}
                        customClass={'hotel-logo dac-logo'}
                        altText={HOTEL_LOGO_ALT_TEXT}
                      />
                    </a>
                  )}
                </div>
                <NavigationWrapper
                  isAuthorMode={isAuthorMode}
                  cqPath={cqPath}
                  model={primaryNavigationProps}
                  utilityNavigation={utilityNavigation}
                  setIsMegaMenuActive={setIsMegaMenuActive}
                  isBrandedVariation={isBrandedVariation}
                  featureFlagLogo={props?.featureFlagLogo}
                  featureFlagLegacyNav={props?.featureFlagLegacyNav}
                />
                {utilityNavigation?.[':items'] && (
                  <NavigationUtilityLinkWrapper
                    utilityNavigation={utilityNavigation}
                    isRenderedInsideOverlay={false}
                    isAuthorMode={isAuthorMode}
                    cqPath={cqPath}
                    logoUrl={logoUrl}
                    logoImageAltText={logoImageAltText}
                    logoType={logoType}
                    isBrandedVariation={isBrandedVariation}
                    isSimplifiedNavigation={isSimplifiedNavigation}
                    featureFlagLegacyNav={props?.featureFlagLegacyNav}
                  />
                )}
              </div>
            </div>
          </header>
        </StyledNavigationContainer>
        {
          // When the absolutely positioned non-transparent header goes sticky,
          // we need to continue accounting for that space
          sticky && !isSearchPresent && !isTransparentHeader && <StyledNavigationPlaceholder />
        }
        {isMyTripsModalOpen && <MyTripsModal />}
      </LanguageSelectorContext.Provider>
    </NavigationContext.Provider>
  );
};
